// Home: the day's progress (on the phone; beside a list it is in the list's bar), and the lists in the order of
// core/sidebar.ts (docs/61, SidebarLayout.swift): Inbox, Today and Deadlines; Favorites under a heading that folds;
// Scheduled, Anytime and Someday; Miscellaneous and the areas, each a heading that folds over its projects and lists;
// Stale, Templates and the Archive. Only what the user made has a heading (Favorites with ⭐ in the place of an
// area's icon); the app's own lists stand in blocks between lines. Every list and project is a place to drop to-dos on.

import { Fragment, useCallback, useState, type MouseEvent, type ReactNode, type RefObject } from "react";
import type { Route } from "../core/lists";
import { isActiveProject } from "../core/lists";
import type { Area, Project } from "../core/records";
import { SIDEBAR_AREAS_LEAD, SIDEBAR_PARTS } from "../core/sidebar";
import { t } from "../i18n";
import { AreaIconPopover, NoIconMark } from "./AreaIcon";
import { useWindow } from "./context";
import { bothDropProps, dropProps, placeAfter, placeDragProps, placeDropProps } from "./dnd";
import { routeIcon, routeTitle, sameRoute, splitEmoji } from "./format";
import { Icon, Ring } from "./Icon";
import { setPrefs, usePrefs } from "./prefs";
import { InvitationCards } from "./Sharing";

export function Sidebar() {
  const window = useWindow();
  const { state, ui, layout, go, showSheet, model } = window;
  const { lists } = state;
  const [showsWaiting, setShowsWaiting] = useState<ReadonlySet<string>>(new Set());
  // The area whose icon is being chosen from its dashed square, and the square.
  const [picking, setPicking] = useState<{ areaID: string; anchor: RefObject<HTMLElement | null> } | null>(null);
  const closePicker = useCallback(() => setPicking(null), []);
  const progress = lists.todayProgress;
  const prefs = usePrefs();
  // Which of Someday, Stale and the Archive Home shows is a Pro customization; without Pro, all of them.
  const shows = state.isPro ? prefs.homeShows : { someday: true, stale: true, archive: true };
  const favoritesFolded = prefs.favoritesFolded;
  // A template's page keeps Templates chosen.
  const isSelected = (route: Route) => !layout.narrow && !ui.finding
    && (sameRoute(ui.route, route) || (route.kind === "templates" && ui.route.kind === "template"));

  // A list on Home. `isKey`: Inbox, Today and Due, whose icons keep their colour and whose counts lead to what waits;
  // the others' icons are grey, green while their list is on show.
  const navRow = (route: Route, count = 0, isKey = false) => (
    <button key={route.kind} className={`nav-row list-row${isKey ? " key" : ""}${route.kind === "due" ? " due" : ""}${route.kind === "habits" ? " habits" : ""}${route.kind === "today" ? " today" : ""}${isSelected(route) ? " selected" : ""}`}
      onClick={() => go(route)} aria-current={isSelected(route) ? "page" : undefined}
      aria-label={isKey ? `${routeTitle(route, lists)}, ${count}` : undefined} {...dropProps(window, route)}>
      <span className="list-icon"><Icon name={routeIcon(route)} /></span>
      {routeTitle(route, lists)}
      {count > 0 && <span className="count">{count}</span>}
    </button>
  );

  // In an area, a project is picked up and moved among its area's projects, or into another area (by its heading).
  const projectRow = (project: Project, count?: number, area?: Area) => {
    const route: Route = { kind: "project", id: project.id };
    const placeProps = area ? {
      ...placeDragProps(window, { kind: "project", id: project.id }),
      ...bothDropProps(dropProps(window, route), placeDropProps({ kind: "project", id: project.id },
        (moving) => (moving.kind === "project" ? "around" : null),
        (moving, where) => {
          // In the order the move rule counts them: every open project of the area.
          const siblings = state.records.projects.filter((other) => other.areaID === area.id && other.completedAt === undefined)
            .sort((a, b) => a.order - b.order);
          model.moveProject(moving.id, where === "before" ? project.id : placeAfter(siblings, project.id, moving.id), area.id);
        })),
    } : dropProps(window, route);
    return (
      <button key={project.id} className={`nav-row${isSelected(route) ? " selected" : ""}`} onClick={() => go(route)}
        aria-current={isSelected(route) ? "page" : undefined} {...placeProps}>
        <ProjectMark project={project} progress={lists.progress(project)} />
        <span className="nav-title">{project.title}</span>
        {count !== undefined && count > 0 && <span className="count">{count}</span>}
      </button>
    );
  };

  // An area's icon with its name on one baseline, at the name's size; without an icon a dashed square, which opens the
  // icon popover for the area's owner.
  const iconSlot = (area: Area, emoji: string | undefined) => {
    if (emoji) return <span className="emoji">{emoji}</span>;
    const share = model.shareOf({ kind: "area", id: area.id });
    const owns = share === null || share.youOwn;
    const open = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      const anchor = { current: event.currentTarget as HTMLElement };
      setPicking((current) => (current?.areaID === area.id ? null : { areaID: area.id, anchor }));
    };
    return (
      <span className={`emoji icon-slot${owns ? " can-pick" : ""}`} title={owns ? t("Choose Icon") : undefined} onClick={owns ? open : undefined}>
        <NoIconMark size={15} />
      </span>
    );
  };

  const favorites = state.isPro
    ? state.records.favorites.flatMap((favorite) => {
      if (favorite.kind === "project") {
        const project = lists.project(favorite.id);
        // No count: a favorite's ring says how far it is.
        return project && project.completedAt === undefined ? [{ key: project.id, row: projectRow(project) }] : [];
      }
      if (favorite.kind === "area") {
        const area = lists.area(favorite.id);
        return area ? [{ key: area.id, row: areaLink(area) }] : [];
      }
      return [];
    })
    : [];

  // An area among the favorites: a row like a project's, the area's icon in the icon's place.
  function areaLink(area: Area) {
    const route: Route = { kind: "area", id: area.id };
    const { emoji, name } = splitEmoji(area.title);
    return (
      <button key={area.id} className={`nav-row${isSelected(route) ? " selected" : ""}`} onClick={() => go(route)} {...dropProps(window, route)}>
        <span className="on-baseline">{iconSlot(area, emoji)}<span className="nav-title">{name}</span></span>
      </button>
    );
  }

  const toggleArea = (area: Area) => window.model.updateArea({ ...area, isCollapsed: !area.isCollapsed });
  const toggleFavorites = () => setPrefs((all) => ({ ...all, favoritesFolded: !all.favoritesFolded }));

  // A list of the app's own; those Home may leave out (Someday, Stale, the Archive) only when it shows them.
  const listRow = (route: Route): ReactNode => {
    switch (route.kind) {
      case "inbox": case "today": return navRow(route, lists.count(route), true);
      case "due": return navRow(route, lists.dueCount, true);
      // Habits (docs/63): after Today, with today's habits still open; the habits themselves live in Today, Habits and
      // their area, not here (Bastian, 2026-10-25).
      case "habits": return navRow(route, lists.count(route), true);
      case "someday": return shows.someday ? navRow(route) : null;
      // Stale counts: what it holds is to be looked at.
      case "stale": return shows.stale ? navRow(route, lists.count(route)) : null;
      case "archive": return shows.archive ? navRow(route) : null;
      default: return navRow(route);
    }
  };

  // Favorites: a heading like an area's (a yellow star in the icon's place, the chevron on the right) that folds and opens nothing.
  const favoritesGroup = (
    <div className={`area favorites${favoritesFolded ? " collapsed" : ""}`}>
      <div className="area-head">
        <button className="area-row" onClick={toggleFavorites} aria-expanded={!favoritesFolded}>
          <span className="on-baseline"><span className="emoji"><Icon name="starFill" className="favorites-star" /></span><span className="nav-title">{t("Favorites")}</span></span>
        </button>
        <button className="area-fold" onClick={toggleFavorites} aria-expanded={!favoritesFolded} tabIndex={-1}
          aria-label={favoritesFolded ? t("Expand {name}", { name: t("Favorites") }) : t("Collapse {name}", { name: t("Favorites") })}>
          <Icon name="chevDown" className="chev" />
        </button>
      </div>
      <div className="area-items">{favorites.map((favorite) => favorite.row)}</div>
    </div>
  );

  const areaGroup = (area: Area) => {
    const all = lists.projectsIn(area).filter((project) => project.isOff !== true);
    const active = all.filter((project) => isActiveProject(project, lists.today));
    const waiting = all.filter((project) => !isActiveProject(project, lists.today));
    const { emoji, name } = splitEmoji(area.title);
    const route: Route = { kind: "area", id: area.id };
    return (
      <Fragment key={area.id}>
        <div className="divider" />
        <div className={`area${area.isCollapsed ? " collapsed" : ""}`}>
          <div className="area-head">
            <button className={`area-row${isSelected(route) ? " selected" : ""}`} onClick={() => go(route)}
              {...placeDragProps(window, { kind: "area", id: area.id })}
              {...bothDropProps(dropProps(window, route), placeDropProps({ kind: "area", id: area.id },
                // An area moves among the areas; a project dropped on an area's heading goes into it, last.
                (moving) => (moving.kind === "area" ? "around" : moving.kind === "project" ? "into" : null),
                (moving, where) => {
                  if (moving.kind === "project") return model.moveProject(moving.id, undefined, area.id);
                  model.moveArea(moving.id, where === "before" ? area.id : placeAfter(lists.sortedAreas, area.id, moving.id));
                }))}>
              <span className="on-baseline">{iconSlot(area, emoji)}<span className="nav-title">{name}</span></span>
            </button>
            {/* The chevron folds the area's projects; an area without any (none starting later either) has none. */}
            {all.length > 0 && (
              <button className="area-fold" onClick={() => toggleArea(area)} aria-expanded={!area.isCollapsed}
                aria-label={area.isCollapsed ? t("Expand {name}", { name }) : t("Collapse {name}", { name })}>
                <Icon name="chevDown" className="chev" />
              </button>
            )}
          </div>
          <div className="area-items">
            {active.map((project) => projectRow(project, undefined, area))}
            {waiting.length > 0 && (
              <>
                <button className="nav-row quiet" aria-expanded={showsWaiting.has(area.id)}
                  onClick={() => setShowsWaiting((current) => {
                    const next = new Set(current);
                    if (next.has(area.id)) next.delete(area.id);
                    else next.add(area.id);
                    return next;
                  })}>
                  <Icon name="clock" />
                  <span>{t("{count} starting later", { count: waiting.length })}</span>
                  <span className={`count waiting-chev${showsWaiting.has(area.id) ? " open" : ""}`}><Icon name="chevRight" /></span>
                </button>
                {showsWaiting.has(area.id) && waiting.map((project) => projectRow(project, undefined, area))}
              </>
            )}
          </div>
        </div>
      </Fragment>
    );
  };

  // The blocks, a line between each two that show something.
  const blocks = SIDEBAR_PARTS.map((part, index): ReactNode => {
    switch (part.kind) {
      case "lists": {
        const rows = part.routes.map(listRow).filter((row) => row !== null);
        // What waits, always on top: where to-dos arrive, what is for today, what is due.
        return index === 0 ? <div className="key-lists">{rows}</div> : rows.length > 0 ? rows : null;
      }
      case "favorites": return favorites.length > 0 ? favoritesGroup : null;
      // No count on Miscellaneous: it is a place, not a queue.
      case "areas": return <>{navRow(SIDEBAR_AREAS_LEAD)}{lists.sortedAreas.map(areaGroup)}</>;
    }
  }).filter((block) => block !== null);
  const pickingArea = picking ? lists.area(picking.areaID) : undefined;

  return (
    <aside className="sidebar" aria-label={t("Home")}>
      <div className="home-bar">
        <h1 className="home-title">{t("Home")}</h1>
        <button className="glass" aria-label={t("Assistant")} title={t("Assistant")}
          onClick={() => showSheet(state.isPro ? { kind: "assistant" } : { kind: "pro", feature: "assistant" })}>
          <Icon name="sparkle" />
        </button>
        <button className="glass wide-only" aria-label={t("New Project")} title={t("New Project")} onClick={() => showSheet({ kind: "newProject" })}>
          <Icon name="folderPlus" />
        </button>
        <button className="glass" aria-label={t("Settings")} title={t("Settings")} onClick={() => showSheet({ kind: "settings" })}>
          <Icon name="gear" />
        </button>
      </div>
      <div className="progress">
        {progress.total === 0 ? (
          <div className="progress-label">{t("Nothing planned for today")}</div>
        ) : (
          <>
            {/* In words, without a bar, as on the iPhone's Home (the hierarchy review, Bastian 2026-11-04). */}
            <div className="progress-label">{t("{done} of {total} done today", { done: progress.done, total: progress.total })}</div>
          </>
        )}
      </div>
      {/* Invitations to shared lists, projects and areas wait here (docs/43). */}
      <InvitationCards />
      {blocks.map((block, index) => (
        <Fragment key={index}>
          {index > 0 && <div className="divider" />}
          {block}
        </Fragment>
      ))}
      {picking && pickingArea && <AreaIconPopover anchor={picking.anchor} area={pickingArea} onClose={closePicker} />}
    </aside>
  );
}

export function ProjectMark({ project, progress, size = 21 }: { project: Project; progress: number; size?: number }) {
  if (project.kind === "birthdays") return <span className="project-mark" style={{ width: size }}><Icon name="gift" /></span>;
  if (project.kind === "list") return <span className="project-mark" style={{ width: size }}><Icon name="list" /></span>;
  return <Ring progress={progress} size={size} />;
}
